/**
 * AffiliationsForm Component
 * 
 * Handles the second step of the registration process, collecting primary and secondary
 * institutional affiliations including addresses and contact information.
 * 
 * @param {Object} props - Component props
 * @param {Object} props.formData - Current form data state
 * @param {Function} props.handleChange - Function to handle form field changes
 * @returns {JSX.Element} Affiliations form step
 */

import React, { useState, useEffect } from 'react';
import { usStates, countries } from '../../constants/types';

const AffiliationsForm = ({ formData, handleChange }) => {
  const [showSecondaryAffiliation, setShowSecondaryAffiliation] = useState(false);
  const [showPrimaryOtherRank, setShowPrimaryOtherRank] = useState(false);
  const [showSecondaryOtherRank, setShowSecondaryOtherRank] = useState(false);

  const academicRanks = [
    "Professor",
    "Associate Professor", 
    "Assistant Professor",
    "Lecturer",
    "Senior Lecturer",
    "Instructor",
    "PhD Student",
    "Other"
  ];

  useEffect(() => {
    // Initialize secondary affiliation visibility based on form data
    const hasSecondaryData = formData.secondaryInstitution || 
                            formData.secondaryRank || 
                            formData.secondaryAddress1 ||
                            formData.secondaryDepartment ||
                            formData.secondaryCity ||
                            formData.secondaryCountry;
    setShowSecondaryAffiliation(!!hasSecondaryData);

    // Check if "Other" is selected for primary rank
    if (formData.primaryRank === "Other") {
      setShowPrimaryOtherRank(true);
    } else {
      setShowPrimaryOtherRank(false);
    }

    // Check if "Other" is selected for secondary rank
    if (formData.secondaryRank === "Other") {
      setShowSecondaryOtherRank(true);
    } else {
      setShowSecondaryOtherRank(false);
    }
  }, [formData.primaryRank, formData.secondaryRank, formData.secondaryInstitution, formData.secondaryAddress1, formData.secondaryDepartment, formData.secondaryCity, formData.secondaryCountry]);

  const handleSecondaryAffiliationToggle = (e) => {
    const isChecked = e.target.checked;
    setShowSecondaryAffiliation(isChecked);
    
    // If unchecking, clear all secondary affiliation fields
    if (!isChecked) {
      const secondaryFields = [
        'secondaryInstitution',
        'secondaryRank',
        'secondaryRankOther',
        'secondaryAddress1',
        'secondaryDepartment',
        'secondaryAddress2',
        'secondaryCity',
        'secondaryPostalCode',
        'secondaryCountry',
        'secondaryState'
      ];
      
      secondaryFields.forEach(field => {
        handleChange({
          target: {
            name: field,
            value: ''
          }
        });
      });
    }
  };

  const handleRankChange = (e) => {
    const { name, value } = e.target;
        
    // Handle the rank change first
    handleChange(e);
    
    // Clear "Other" field if rank is changed to something other than "Other"
    if (value !== "Other") {
      if (name === 'primaryRank') {
        handleChange({
          target: {
            name: 'primaryRankOther',
            value: ''
          }
        });
      } else if (name === 'secondaryRank') {
        handleChange({
          target: {
            name: 'secondaryRankOther',
            value: ''
          }
        });
      }
    }
  };
  return (
    <div>
      <h4 className="mb-4">Affiliations</h4>

      <h5 className="text-conference-primary mb-3">Primary Affiliation</h5>

      <div className="row">
        <div className="col-md-6 mb-3">
          <label htmlFor="primaryInstitution" className="form-label">
            Institution <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            className="form-control"
            id="primaryInstitution"
            name="primaryInstitution"
            value={formData.primaryInstitution}
            onChange={handleChange}
            required
          />
        </div>

        <div className="col-md-6 mb-3">
          <label htmlFor="primaryRank" className="form-label">
            Academic Rank <span className="text-danger">*</span>
          </label>
          <select
            className="form-select"
            id="primaryRank"
            name="primaryRank"
            value={formData.primaryRank}
            onChange={handleRankChange}
            required
          >
            <option value="">Select rank...</option>
            {academicRanks.map((rank) => (
              <option key={rank} value={rank}>{rank}</option>
            ))}
          </select>
          
          {showPrimaryOtherRank && (
            <div className="mt-2">
              <label htmlFor="primaryRankOther" className="form-label">
                Please specify other rank
              </label>
              <input
                type="text"
                className="form-control"
                id="primaryRankOther"
                name="primaryRankOther"
                value={formData.primaryRankOther || ''}
                onChange={handleChange}
                placeholder="Please specify your rank"
              />
            </div>
          )}
        </div>
      </div>

      <div className="row">
        <div className="col-md-6 mb-3">
          <label htmlFor="primaryAddress1" className="form-label">
            Address 1 <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            className="form-control"
            id="primaryAddress1"
            name="primaryAddress1"
            value={formData.primaryAddress1}
            onChange={handleChange}
            required
          />
        </div>

        <div className="col-md-6 mb-3">
          <label htmlFor="primaryDepartment" className="form-label">
            Department/Schools
          </label>
          <input
            type="text"
            className="form-control"
            id="primaryDepartment"
            name="primaryDepartment"
            value={formData.primaryDepartment}
            onChange={handleChange}
          />
        </div>
      </div>

      <div className="row">
        <div className="col-md-6 mb-3">
          <label htmlFor="primaryAddress2" className="form-label">
            Address 2
          </label>
          <input
            type="text"
            className="form-control"
            id="primaryAddress2"
            name="primaryAddress2"
            value={formData.primaryAddress2}
            onChange={handleChange}
          />
        </div>
        <div className="col-md-6 mb-3">
          <label htmlFor="primaryCity" className="form-label">
            City <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            className="form-control"
            id="primaryCity"
            name="primaryCity"
            value={formData.primaryCity}
            onChange={handleChange}
            required
          />
        </div>
      </div>

      <div className="row">
        <div className="col-md-6 mb-3">
          <label htmlFor="primaryPostalCode" className="form-label">
            Postal / Zip Code <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            className="form-control"
            id="primaryPostalCode"
            name="primaryPostalCode"
            value={formData.primaryPostalCode}
            onChange={handleChange}
            required
          />
        </div>

        <div className="col-md-6 mb-3">
          <label htmlFor="primaryCountry" className="form-label">
            Country <span className="text-danger">*</span>
          </label>
          <select
            className="form-select"
            id="primaryCountry"
            name="primaryCountry"
            value={formData.primaryCountry}
            onChange={handleChange}
            required
          >
            <option value="">Select Country...</option>
            {countries.map((country) => (
              <option key={country} value={country}>
                {country}
              </option>
            ))}
          </select>
        </div>
      </div>

      {formData.primaryCountry === "United States" && (
        <div className="row">
          <div className="col-md-6 mb-3">
            <label htmlFor="primaryState" className="form-label">
              State (US Only)
            </label>
            <select
              className="form-select"
              id="primaryState"
              name="primaryState"
              value={formData.primaryState}
              onChange={handleChange}
            >
              <option value="">Select State...</option>
              {usStates.map((state) => (
                <option key={state} value={state}>
                  {state}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      <hr className="my-4" />

      <div className="mb-3">
        <div className="form-check">
          <input
            className="form-check-input"
            type="checkbox"
            id="showSecondaryAffiliation"
            checked={showSecondaryAffiliation}
            onChange={handleSecondaryAffiliationToggle}
          />
          <label className="form-check-label" htmlFor="showSecondaryAffiliation">
            Enter Secondary Affiliation Details
          </label>
        </div>
        <small className="form-text text-muted">
          Check this box if you have a secondary institutional affiliation to add.
        </small>
      </div>

      {showSecondaryAffiliation && (
        <div>
          <h5 className="text-conference-primary mb-3">Secondary Affiliation</h5>

      <div className="row">
        <div className="col-md-6 mb-3">
          <label htmlFor="secondaryInstitution" className="form-label">
            Institution <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            className="form-control"
            id="secondaryInstitution"
            name="secondaryInstitution"
            value={formData.secondaryInstitution}
            onChange={handleChange}
            required
          />
        </div>

        <div className="col-md-6 mb-3">
          <label htmlFor="secondaryRank" className="form-label">
            Academic Rank <span className="text-danger">*</span>
          </label>
          <select
            className="form-select"
            id="secondaryRank"
            name="secondaryRank"
            value={formData.secondaryRank}
            onChange={handleRankChange}
            required
          >
            <option value="">Select rank...</option>
            {academicRanks.map((rank) => (
              <option key={rank} value={rank}>{rank}</option>
            ))}
          </select>
          
          {showSecondaryOtherRank && (
            <div className="mt-2">
              <label htmlFor="secondaryRankOther" className="form-label">
                Please specify other rank
              </label>
              <input
                type="text"
                className="form-control"
                id="secondaryRankOther"
                name="secondaryRankOther"
                value={formData.secondaryRankOther || ''}
                onChange={handleChange}
                placeholder="Please specify your rank"
              />
            </div>
          )}
        </div>
      </div>

      <div className="row">
        <div className="col-md-6 mb-3">
          <label htmlFor="secondaryAddress1" className="form-label">
            Address 1 <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            className="form-control"
            id="secondaryAddress1"
            name="secondaryAddress1"
            value={formData.secondaryAddress1}
            onChange={handleChange}
            required
          />
        </div>

        <div className="col-md-6 mb-3">
          <label htmlFor="secondaryDepartment" className="form-label">
            Department/Schools
          </label>
          <input
            type="text"
            className="form-control"
            id="secondaryDepartment"
            name="secondaryDepartment"
            value={formData.secondaryDepartment}
            onChange={handleChange}
          />
        </div>
      </div>

      <div className="row">
        <div className="col-md-6 mb-3">
          <label htmlFor="secondaryAddress2" className="form-label">
            Address 2
          </label>
          <input
            type="text"
            className="form-control"
            id="secondaryAddress2"
            name="secondaryAddress2"
            value={formData.secondaryAddress2}
            onChange={handleChange}
          />
        </div>
        <div className="col-md-6 mb-3">
          <label htmlFor="secondaryCity" className="form-label">
            City <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            className="form-control"
            id="secondaryCity"
            name="secondaryCity"
            value={formData.secondaryCity}
            onChange={handleChange}
            required
          />
        </div>
      </div>

      <div className="row">
        <div className="col-md-6 mb-3">
          <label htmlFor="secondaryPostalCode" className="form-label">
            Postal / Zip Code <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            className="form-control"
            id="secondaryPostalCode"
            name="secondaryPostalCode"
            value={formData.secondaryPostalCode}
            onChange={handleChange}
            required
          />
        </div>

        <div className="col-md-6 mb-3">
          <label htmlFor="secondaryCountry" className="form-label">
            Country <span className="text-danger">*</span>
          </label>
          <select
            className="form-select"
            id="secondaryCountry"
            name="secondaryCountry"
            value={formData.secondaryCountry}
            onChange={handleChange}
            required
          >
            <option value="">Select Country...</option>
            {countries.map((country) => (
              <option key={country} value={country}>
                {country}
              </option>
            ))}
          </select>
        </div>
      </div>

      {formData.secondaryCountry === "United States" && (
        <div className="row">
          <div className="col-md-6 mb-3">
            <label htmlFor="secondaryState" className="form-label">
              State (US Only)
            </label>
            <select
              className="form-select"
              id="secondaryState"
              name="secondaryState"
              value={formData.secondaryState}
              onChange={handleChange}
            >
              <option value="">Select State...</option>
              {usStates.map((state) => (
                <option key={state} value={state}>
                  {state}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}
        </div>
      )}
    </div>
  );
};

export default AffiliationsForm;
